import type { Locale } from '@susu/custom-made/i18n-config';
import type { ClientType } from '@susu/custom-made/utils/constants';
import { Clients } from '@susu/custom-made/utils/constants';
import type {
  ConfigurationTypeImages,
  DeliveryPeriod,
  SizePassportSelection,
} from '@susu/custom-made/utils/interfaces';
import type { ProductType } from '@susu/custom-made/utils/productDefinition';

import type { State } from '..';

export enum actions {
  UPDATE_PRICE = 'UPDATE_PRICE',
  SELECT_SIZE_PASSPORT_ID = 'SELECT_SIZE_PASSPORT_ID',
  SET_TOKEN = 'SET_TOKEN',
  SET_ACCOUNT_NUMBER = 'SET_ACCOUNT_NUMBER',
  SET_STORE_ID = 'SET_STORE_ID',
  SET_PARAMS = 'SET_PARAMS',
  SET_CONFIGURATION_TYPE_IMAGE = 'SET_CONFIGURATION_TYPE_IMAGE',
  SET_DELIVERY_PERIOD = 'SET_DELIVERY_PERIOD',
  POPULATE_STATE = 'POPULATE_STATE',
  SET_SIZE_ID = 'SET_SIZE_ID',
  SET_MANUAL_SELECTION = 'SET_MANUAL_SELECTION',
  SET_SP_CORRELATION_ID = 'SET_SP_CORRELATION_ID',
  SET_PREVIEW_PANNING = 'SET_PREVIEW_PANNING',
  SET_PREVIEW_ZOOM = 'SET_PREVIEW_ZOOM',
  SET_IS_USER_PRESELECTED = 'SET_IS_USER_PRESELECTED',
}

export interface GlobalStateProps {
  totalPrice: number;
  sizePassportSelection: SizePassportSelection;
  token?: string;
  accountNr?: string;
  countryCode: string;
  client: ClientType;
  language: Locale;
  product: ProductType | undefined;
  configurationTypeImages: ConfigurationTypeImages;
  storeID?: string;
  deliveryPeriod: DeliveryPeriod | undefined;
  sizeId?: string;
  isManualSelectionDone: boolean;
  spCorrelationId?: string;
  isPreviewPanning: boolean;
  isPreviewZoomed: boolean;
  isUserPreselected: boolean;
  growthbookFeatures: Record<string, any>;
}

export const initialState: GlobalStateProps = {
  totalPrice: 0,
  sizePassportSelection: {},
  token: undefined,
  accountNr: undefined,
  countryCode: 'NL',
  client: Clients.onLine as ClientType,
  language: 'en',
  product: undefined,
  configurationTypeImages: {},
  storeID: undefined,
  deliveryPeriod: undefined,
  sizeId: undefined,
  isManualSelectionDone: false,
  spCorrelationId: crypto.randomUUID(),
  isPreviewPanning: false,
  isPreviewZoomed: false,
  isUserPreselected: false,
  growthbookFeatures: {},
};

interface UpdatePriceAction {
  type: actions.UPDATE_PRICE;
  payload: number;
}

interface SelectSizePassportIdAction {
  type: actions.SELECT_SIZE_PASSPORT_ID;
  payload: SizePassportSelection;
}

interface SetAccountNumberAction {
  type: actions.SET_ACCOUNT_NUMBER;
  payload: string;
}

interface SetTokenAction {
  type: actions.SET_TOKEN;
  payload: string;
}

interface SetStoreIDAction {
  type: actions.SET_STORE_ID;
  payload: string;
}

interface SetParamsAction {
  type: actions.SET_PARAMS;
  payload: {
    countryCode: string;
    client: ClientType;
    language: Locale;
    product: ProductType;
  };
}

interface SetConfigurationTypeImages {
  type: actions.SET_CONFIGURATION_TYPE_IMAGE;
  payload: ConfigurationTypeImages;
}

interface SetDeliveryPeriodAction {
  type: actions.SET_DELIVERY_PERIOD;
  payload: DeliveryPeriod;
}

interface PopulateStateAction {
  type: actions.POPULATE_STATE;
  payload: State;
}

interface SetSizeIdAction {
  type: actions.SET_SIZE_ID;
  payload: string;
}

interface SetManualSelectionAction {
  type: actions.SET_MANUAL_SELECTION;
  payload: boolean;
}

interface SetSPCorrelationIdAction {
  type: actions.SET_SP_CORRELATION_ID;
  payload: string;
}

interface SetPreviewPanning {
  type: actions.SET_PREVIEW_PANNING;
  payload: boolean;
}

interface SetPreviewZoom {
  type: actions.SET_PREVIEW_ZOOM;
  payload: boolean;
}

interface SetIsUserPreselected {
  type: actions.SET_IS_USER_PRESELECTED;
  payload: boolean;
}

export type GlobalActionTypes =
  | UpdatePriceAction
  | SelectSizePassportIdAction
  | SetAccountNumberAction
  | SetTokenAction
  | SetStoreIDAction
  | SetParamsAction
  | SetConfigurationTypeImages
  | SetDeliveryPeriodAction
  | PopulateStateAction
  | SetSizeIdAction
  | SetManualSelectionAction
  | SetSPCorrelationIdAction
  | SetPreviewPanning
  | SetPreviewZoom
  | SetIsUserPreselected;

export default function GlobalReducer(state: GlobalStateProps, action: GlobalActionTypes) {
  switch (action.type) {
    case actions.UPDATE_PRICE:
      return { ...state, totalPrice: action.payload };

    case actions.SELECT_SIZE_PASSPORT_ID:
      return { ...state, sizePassportSelection: action.payload };

    case actions.SET_ACCOUNT_NUMBER:
      return { ...state, accountNr: action.payload };

    case actions.SET_TOKEN:
      return { ...state, token: action.payload };

    case actions.SET_STORE_ID:
      return { ...state, storeID: action.payload };

    case actions.SET_PARAMS:
      return {
        ...state,
        countryCode: action.payload.countryCode,
        client: action.payload.client,
        language: action.payload.language,
        product: action.payload.product,
      };

    case actions.SET_CONFIGURATION_TYPE_IMAGE:
      return {
        ...state,
        configurationTypeImages: {
          ...state.configurationTypeImages,
          ...action.payload,
        },
      };
    case actions.POPULATE_STATE:
      return {
        ...state,
        ...action.payload,
      };
    case actions.SET_DELIVERY_PERIOD:
      return {
        ...state,
        deliveryPeriod: action.payload,
      };

    case actions.SET_SIZE_ID:
      return {
        ...state,
        sizeId: action.payload,
      };

    case actions.SET_MANUAL_SELECTION:
      return {
        ...state,
        isManualSelectionDone: action.payload,
      };

    case actions.SET_SP_CORRELATION_ID:
      return {
        ...state,
        spCorrelationId: action.payload,
      };
    case actions.SET_PREVIEW_PANNING:
      return {
        ...state,
        isPreviewPanning: action.payload,
      };
    case actions.SET_PREVIEW_ZOOM:
      return {
        ...state,
        isPreviewZoomed: action.payload,
      };
    case actions.SET_IS_USER_PRESELECTED:
      return {
        ...state,
        isUserPreselected: action.payload,
      };

    default:
      return state;
  }
}
